import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import { Card, Button } from "reactstrap";
import { Tag, Flex, Spin } from "antd";
import { Collapse } from "antd";
import {
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../../components/Component";
import GeneralInfoSection from "./GeneralInfoSection";
import MaterialInfoSection from "./MaterialInfoSection";
import WorkInfoSection from "./WorkInfoSection";
import EvaluationQuestionersSection from "./EvaluationQuestionersSection";
import tenderApis from "../../../api/tender/tenderApis";

const { Panel } = Collapse;

const renderTenderForTag = (text: string) => {
  if (!text) return null;
  if (text.toLowerCase() === "material" || text.toLowerCase() === "materials") {
    return <Tag color="purple">{text}</Tag>;
  }
  if (text.toLowerCase() === "service" || text.toLowerCase() === "services") {
    return <Tag color="cyan">{text}</Tag>;
  }
  return <Tag>{text}</Tag>;
};

const SupplierViewPage: React.FC = () => {
  const { tenderId, supId } = useParams<{
    tenderId: string;
    supId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as any;

  const [loading, setLoading] = useState(true);
  const [tenderData, setTenderData] = useState<any>(null);

  const fetchTenderWithBid = async (silent = false) => {
    const bidId = locationState?.bidId || Number(supId);
    const bidType = locationState?.bidType || "Supplier";
    const tId = locationState?.tenderId || Number(tenderId);

    if (!silent) setLoading(true);
    try {
      const res = await tenderApis.getTenderWithBid(tId, bidId, bidType);
      if (res?.status && res?.data) {
        setTenderData(res.data);
      }
    } catch (error) {
      console.error("Error fetching tender with bid:", error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const refreshData = () => fetchTenderWithBid(true);

  useEffect(() => {
    fetchTenderWithBid();
  }, [tenderId, supId]);

  if (loading) {
    return (
      <>
        <Head title="Supplier View" />
        <Content>
          <Card className="card-bordered">
            <Flex
              align="center"
              justify="center"
              style={{ minHeight: 400 }}
            >
              <Spin size="large" />
            </Flex>
          </Card>
        </Content>
      </>
    );
  }

  const tenderFor = tenderData?.tenderFor || "";
  const showMaterialInfo = tenderFor === "Material";
  const showWorkInfo = tenderFor === "Service";

  const materials = tenderData?.materials || [];
  const tasks = tenderData?.tasks || [];
  const questionnaireSections = tenderData?.questionnaireSections || [];
  const evaluationApprovals = tenderData?.evaluationApprovals || [];
  const bidDetails = tenderData?.bidDetails || {};

  // Auto-generate options based on question type when API returns empty options
  const getOptionsForType = (type: string, options: any[]) => {
    if (options && options.length > 0) return options;
    switch (type) {
      case "Yes/No":
        return ["Yes", "No"];
      case "Single Choice":
      case "Multiple Choice":
        return options || [];
      default:
        return [];
    }
  };

  // Transform API questionnaireSections to the format expected by EvaluationQuestionersSection
  const questionnaireResponses = questionnaireSections.map((section: any) => ({
    sectionName: section.sectionName,
    sectionId: section.tenderSectionId,
    sectionWeightage: section.weightage,
    evaluatorId: section.evaluators?.[0]?.evaluatorId || section.evaluators?.[0]?.userId,
    evaluators: section.evaluators || [],
    approvals: (section.evaluators || []).map((ev: any) => ({
      name: ev.userName || ev.name || "--",
      role: ev.role || ev.level || "--",
      type: ev.type || "--",
      dateTime: ev.dateTime || ev.approvedAt || "",
      status: ev.submissionStatus === "submitted" ? "APPROVED" : ev.status || ev.approvalStatus || "PENDING",
      evaluatorId: ev.tenderSectionEvaluatorId,
    })),
    responses: (section.questions || []).map((q: any) => ({
      questionId: q.tenderQuestionId,
      question: q.question,
      type: q.type,
      options: getOptionsForType(q.type, q.options || []),
      weightage: q.weightage || 0,
      description: q.description || "",
      answer:
        q.type === "Multiple Choice"
          ? q.bidAnswer?.selectedOptions || []
          : q.type === "Yes/No" || q.type === "Single Choice"
          ? q.bidAnswer?.selectedOptions?.[0] || q.bidAnswer?.answerText || "--"
          : q.bidAnswer?.answerText || "--",
      supplierRemarks: q.bidAnswer?.remarks || "",
      comment: "",
    })),
  }));

  return (
    <>
      <Head title="Supplier View" />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div className="card-inner card-inner-lg">
              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">
                      <span>
                        {tenderData?.tenderNumber || tenderId} |{" "}
                        {bidDetails?.bidNumber && (
                          <Tag color="geekblue" style={{ fontWeight: 500 }}>
                            {bidDetails.bidNumber}
                          </Tag>
                        )}
                        <Tag color="blue" style={{ fontWeight: 500 }}>
                          {tenderData?.status || "Under Evaluation"}
                        </Tag>
                        {bidDetails?.supplierName && (
                          <Tag color="default" style={{ fontWeight: 500 }}>
                            {bidDetails.supplierName}
                          </Tag>
                        )}
                      </span>
                    </BlockTitle>
                  </BlockHeadContent>
                  <div className="d-flex">
                    <Button
                      color="primary"
                      onClick={() =>
                        navigate(`/tender-evaluation/${tenderId}`)
                      }
                    >
                      <Icon name="arrow-left" />
                      <span>Back</span>
                    </Button>
                  </div>
                </BlockBetween>
              </BlockHead>

              <div className="content clearfix">
                <Collapse defaultActiveKey={["1", "2", "3", "4"]}>
                  <Panel header="General Information" key="1">
                    <GeneralInfoSection
                      tenderFor={tenderFor}
                      renderTenderForTag={renderTenderForTag}
                      generalApprovals={evaluationApprovals}
                      tenderData={tenderData}
                      bidDetails={bidDetails}
                    />
                  </Panel>
                  {showMaterialInfo && materials.length > 0 && (
                    <Panel header="Material Information" key="2">
                      <MaterialInfoSection materials={materials} />
                    </Panel>
                  )}
                  {showWorkInfo && tasks.length > 0 && (
                    <Panel header="Work / Service Information" key="3">
                      <WorkInfoSection services={tasks} />
                    </Panel>
                  )}
                  {questionnaireSections.length > 0 && (
                    <Panel header="Information for Evaluation" key="4">
                      <EvaluationQuestionersSection
                        supplierId={supId}
                        questionnaireResponses={questionnaireResponses}
                        tenderId={locationState?.tenderId || Number(tenderId)}
                        bidId={locationState?.bidId || Number(supId)}
                        bidType={locationState?.bidType || "Supplier"}
                        onRefresh={refreshData}
                      />
                    </Panel>
                  )}
                </Collapse>
              </div>
            </div>
          </div>
        </Card>
      </Content>
    </>
  );
};

export default SupplierViewPage;
